import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { AdminOverview } from './AdminOverview';
import { AdminPages } from './AdminPages';
import { AdminAdMetrics } from './AdminAdMetrics';
import { AdminAdsToCall } from './AdminAdsToCall';
import { AdminCompare } from './AdminCompare';
import { AdminEmail } from './AdminEmail';
import { AdminRevenue } from './AdminRevenue';
import { AdminUsers } from './AdminUsers';
import { AdminTestimonials } from './AdminTestimonials';
import { AdminHealth } from './AdminHealth';
import { AdminTraffic } from './AdminTraffic';
import { AdminGoals } from './AdminGoals';
import { AdminExport } from './AdminExport';

const FONT = "'DM Sans', system-ui, sans-serif";

const C = {
  bg: '#FFF8F7',
  text: '#3D1F02',
  heading: '#6B3E0A',
  accent: '#AD795B',
  border: '#E8D5C4',
  sidebarBg: '#2A1400',
  sidebarActive: '#6B3E0A',
};

const NAV = [
  { id: 'overview', label: 'Overview',             icon: '▦' },
  { id: 'pages',    label: 'Pages & Masterclass',  icon: '⬡' },
  { id: 'admetrics', label: 'Ad Metrics',           icon: '◇' },
  { id: 'adstocall', label: 'Ads to Call',          icon: '☏' },
  { id: 'compare',  label: 'Compare',              icon: '⇄' },
  { id: 'email',    label: 'Email Stats',           icon: '✉' },
  { id: 'revenue',  label: 'Revenue',               icon: '◈' },
  { id: 'users',    label: 'Users',                 icon: '◉' },
  { id: 'testimonials', label: 'Testimonials',      icon: '★' },
  { id: 'health',   label: 'System Health',         icon: '⬡' },
  { id: 'traffic',  label: 'Traffic & UTM',         icon: '◎' },
  { id: 'goals',    label: 'Goals & Targets',        icon: '◎' },
  { id: 'export',   label: 'Export Report',          icon: '↓' },
];

const SECTION_MAP = {
  overview: <AdminOverview />,
  pages:    <AdminPages />,
  admetrics: <AdminAdMetrics />,
  adstocall: <AdminAdsToCall />,
  compare:  <AdminCompare />,
  email:    <AdminEmail />,
  revenue:  <AdminRevenue />,
  users:    <AdminUsers />,
  testimonials: <AdminTestimonials />,
  health:   <AdminHealth />,
  traffic:  <AdminTraffic />,
  goals:    <AdminGoals />,
  export:   <AdminExport />,
};

const fmtDateTime = () => {
  const now = new Date();
  return now.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' ' + now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

export const AdminDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState('overview');

  if (authLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: C.bg, fontFamily: FONT, color: C.heading }}>
        Loading…
      </div>
    );
  }

  if (!user?.isAdmin) {
    navigate('/dashboard', { replace: true });
    return null;
  }

  return (
    <div style={{ fontFamily: FONT, display: 'flex', minHeight: '100vh', background: C.bg }}>

      {/* ── Sidebar ── */}
      <div style={{
        width: 220,
        background: C.sidebarBg,
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        zIndex: 100,
      }}>
        {/* Logo area */}
        <div style={{ padding: '28px 20px 20px' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#AD795B', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 4 }}>
            The Conscious Creator
          </div>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#FFF8F7' }}>
            Admin Portal
          </div>
          <div style={{ marginTop: 12, height: 1, background: '#3D1F02' }} />
        </div>

        {/* Nav items */}
        <nav style={{ flex: 1, padding: '8px 12px' }}>
          {NAV.map(n => (
            <button
              key={n.id}
              onClick={() => setActive(n.id)}
              style={{
                width: '100%',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 12px',
                borderRadius: 8,
                border: 'none',
                cursor: 'pointer',
                background: active === n.id ? C.sidebarActive : 'transparent',
                color: active === n.id ? '#FFF8F7' : '#AD795B',
                fontSize: 13,
                fontWeight: active === n.id ? 700 : 400,
                marginBottom: 2,
                fontFamily: FONT,
                transition: 'background 0.15s, color 0.15s',
              }}
            >
              <span style={{ opacity: 0.8, fontSize: 14 }}>{n.icon}</span>
              {n.label}
            </button>
          ))}
        </nav>

        {/* Footer */}
        <div style={{ padding: '16px 20px', borderTop: '1px solid #3D1F02' }}>
          <div style={{ fontSize: 10, color: '#6B3E0A' }}>Logged in as</div>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#AD795B' }}>Ben Milston</div>
          <div style={{ fontSize: 10, color: '#6B3E0A', marginTop: 2 }}>Admin · is_admin=true</div>
        </div>
      </div>

      {/* ── Main content ── */}
      <div style={{ marginLeft: 220, flex: 1, display: 'flex', flexDirection: 'column' }}>

        {/* Top bar */}
        <div style={{
          background: '#fff',
          borderBottom: `1px solid ${C.border}`,
          padding: '14px 28px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          zIndex: 50,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {/* Back to Portal — direct shortcut to /dashboard so admin doesn't
                have to go through Client Admin first. */}
            <button
              onClick={() => navigate('/dashboard')}
              style={{
                padding: '7px 14px',
                background: 'none',
                border: `1px solid ${C.border}`,
                color: C.text,
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: FONT,
              }}
            >
              ← Back to Portal
            </button>
            <div>
              <span style={{ fontSize: 13, color: C.accent }}>The Conscious Creator</span>
              <span style={{ fontSize: 13, color: C.border, margin: '0 8px' }}>·</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: C.heading }}>Admin Dashboard</span>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div style={{ fontSize: 11, color: C.accent }}>
              Last updated: {fmtDateTime()}
            </div>
            {/* Client Admin → opens existing Admin.jsx at /admin/clients */}
            <button
              onClick={() => navigate('/admin/clients')}
              style={{
                padding: '7px 14px',
                background: 'none',
                border: `1px solid ${C.border}`,
                color: C.text,
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: FONT,
              }}
            >
              Client Admin →
            </button>
            {/* Health Check shortcut */}
            <button
              onClick={() => setActive('health')}
              style={{
                padding: '7px 16px',
                background: C.heading,
                color: '#FFF8F7',
                border: 'none',
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: FONT,
              }}
            >
              ↻ Health Check
            </button>
          </div>
        </div>

        {/* Section content */}
        <div style={{ padding: '28px 32px', flex: 1 }}>
          {SECTION_MAP[active]}
        </div>
      </div>
    </div>
  );
};
